const previewCache = new Map();
const pendingPreviews = new Map();
let cacheBytes = 0;
let generation = 0;

export const MATPLOTLIB_PREVIEW_CACHE_LIMIT = 70;
// Conservative UTF-16 string estimate; decoded bitmap memory is owned by the
// browser and is separate from this cache's retained source/image strings.
export const MATPLOTLIB_PREVIEW_CACHE_BYTES = 16 * 1024 * 1024;
const entryBytes = (code, image) => (code.length + String(image).length) * 2;

const removeEntry = (code) => {
  if (!previewCache.has(code)) return;
  cacheBytes -= entryBytes(code, previewCache.get(code));
  previewCache.delete(code);
};

const normalizeSourceCode = (code) => String(code || "").trim();

export const getCachedMatplotlibPreview = (code) => {
  const normalizedCode = normalizeSourceCode(code);
  if (!normalizedCode) return "";

  const image = previewCache.get(normalizedCode) || "";
  if (image) {
    previewCache.delete(normalizedCode);
    previewCache.set(normalizedCode, image);
  }
  return image;
};

export const rememberMatplotlibPreview = (code, image) => {
  const normalizedCode = normalizeSourceCode(code);
  if (!normalizedCode || !image) return;

  removeEntry(normalizedCode);
  const size = entryBytes(normalizedCode, image);
  if (size > MATPLOTLIB_PREVIEW_CACHE_BYTES) return;
  previewCache.set(normalizedCode, image);
  cacheBytes += size;
  while (previewCache.size > MATPLOTLIB_PREVIEW_CACHE_LIMIT || cacheBytes > MATPLOTLIB_PREVIEW_CACHE_BYTES) {
    removeEntry(previewCache.keys().next().value);
  }
};

export const getOrRenderMatplotlibPreview = async (code, renderPreview) => {
  const normalizedCode = normalizeSourceCode(code);
  if (!normalizedCode) return { success: false, error: "缺少 Python 程式碼。" };

  const cachedImage = getCachedMatplotlibPreview(normalizedCode);
  if (cachedImage) {
    return { success: true, image: cachedImage, cached: true };
  }

  if (pendingPreviews.has(normalizedCode)) {
    return pendingPreviews.get(normalizedCode);
  }

  const requestGeneration = generation;
  const pendingPreview = Promise.resolve()
    .then(() => renderPreview(normalizedCode))
    .then((result) => {
      if (requestGeneration === generation && result?.success && result.image) {
        rememberMatplotlibPreview(normalizedCode, result.image);
      }
      return result;
    })
    .finally(() => {
      if (pendingPreviews.get(normalizedCode) === pendingPreview) pendingPreviews.delete(normalizedCode);
    });

  pendingPreviews.set(normalizedCode, pendingPreview);
  return pendingPreview;
};

export const clearMatplotlibPreviewCache = () => {
  previewCache.clear();
  pendingPreviews.clear();
  cacheBytes = 0;
  generation += 1;
};
